import { useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Modal, Paper, Typography } from '@mui/material'
import { PurchaseOrderItem } from 'types/graphql'
import * as yup from 'yup'

import { Form, useForm } from '@cedarjs/forms'
import { useMutation } from '@cedarjs/web'
import { toast } from 'react-hot-toast'

import { useDispatch } from 'src/hooks/useDispatch'
import { useSelector } from 'src/hooks/useSelector'
import {
  CREATE_AND_ADD_LINE_ITEM_TO_PURCHASE_ORDER,
  GET_PURCHASE_ORDER,
} from 'src/pages/PurchaseOrderPage/queries'
import { closeModal } from 'src/slices/modal'
import {
  dollarsToCents,
  isDecimalWithTwoOrLessPlacesNullable,
} from 'src/utils/helpers'

import DollarCentsTextField from './DollarCentsTextField'
import Button from './MUI/Button'
import IconButton from './MUI/IconButton'
import TextField from './MUI/TextField'
import Drawer from './MUI/Drawer/Drawer'
import DrawerHeader from './MUI/Drawer/DrawerHeader'
import DrawerActions from './MUI/Drawer/DrawerActions'

type AddLineItemModalProps = {
  purchaseOrderId?: string
  onComplete?: (item: PurchaseOrderItem) => void
  onRequest?: ({ description, cost }) => void
  /** Optionally define what happens when the user clicks backdrop, the close icon, or hits "escape" */
  onBail?: () => void
}

const modalName = 'addLineItemModal'

const schema = yup.object().shape({
  cost: yup
    .number()
    .typeError('Cost is a number')
    .min(0, 'Must be positive')
    .test(
      'valid-decimal',
      'No more than 2 decimal points',
      isDecimalWithTwoOrLessPlacesNullable
    )
    .nullable(),
  description: yup.string(),
})

const AddLineItemModal: React.FC<AddLineItemModalProps> = ({
  purchaseOrderId,
  onComplete,
  onRequest,
  onBail,
}) => {
  const [createAndAddLineItemToPurchaseOrder] = useMutation(
    CREATE_AND_ADD_LINE_ITEM_TO_PURCHASE_ORDER,
    {
      update: (
        cache,
        { data: { createAndAddLineItemToPurchaseOrder: lineItem } }
      ) => {
        const {
          purchaseOrder: { purchaseOrderItem: poItems },
        } = cache.readQuery({
          query: GET_PURCHASE_ORDER,
          variables: {
            id: purchaseOrderId,
          },
        })
        cache.writeQuery({
          query: GET_PURCHASE_ORDER,
          variables: {
            id: purchaseOrderId,
          },
          data: {
            purchaseOrder: {
              purchaseOrderItem: [...poItems, lineItem],
            },
          },
        })
      },
      onCompleted: ({ createAndAddLineItemToPurchaseOrder }) =>
        onComplete?.(createAndAddLineItemToPurchaseOrder),
    }
  )
  const dispatch = useDispatch()
  const formMethods = useForm({ resolver: yupResolver(schema) })

  const open =
    useSelector((state) => state.modal.modals?.[modalName]?.isOpen) ?? false

  useEffect(() => {
    // reset on the closure, so you don't see the label animation on the next open
    if (!open) {
      formMethods.reset({
        cost: null,
        description: '',
      })
    }
  }, [open, formMethods])
  const handleClose = () => {
    dispatch(closeModal({ name: modalName }))
  }

  const handleBail = () => {
    handleClose()
    onBail?.()
  }

  const handleSubmit = async (values) => {
    await createAndAddLineItemToPurchaseOrder({
      variables: {
        input: {
          ...values,
          cost: dollarsToCents(parseFloat(values.cost)),
        },
        purchaseOrderId,
      },
    })
    handleClose()
    toast.success('Line item added')
    onRequest?.(values)
  }

  return (
    <Drawer anchor="right" open={open} onClose={handleBail}>
      <DrawerHeader title="Add Line Item" onClose={handleBail} />
      <div className="h-full w-[800px] p-3">
        <Form
          id="addLineItemForm"
          className="h-full"
          formMethods={formMethods}
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-2">
            <Typography variant="caption" className="text-blueGrey-600">
              DETAILS
            </Typography>
            <div className="flex gap-2">
              <DollarCentsTextField
                name="cost"
                control={formMethods.control}
                label="Cost"
              />
            </div>
            <TextField
              label="Description"
              name="description"
              multiline
              fullWidth
              minRows={4}
            />
          </div>
        </Form>
      </div>
      <DrawerActions
        className="gap-1"
        leftActions={
          <Button variant="text" color="black" onClick={handleBail}>
            CANCEL
          </Button>
        }
        rightActions={
          <Button
            type="submit"
            form="addLineItemForm"
            variant="contained"
            color="primary"
          >
            ADD LINE ITEM
          </Button>
        }
      />
    </Drawer>
  )
}

export default AddLineItemModal
